/**
 * Unbestätigte Absichten für „Druck nachtragen“ — eine Nachtrags-ID samt den
 * gesendeten Angaben, die bis zur Klärung stehen bleibt (je Projekt eine).
 *
 * Modulweit, nicht im Dialog: wer den Dialog nach einem Fehler schließt und
 * neu öffnet, speicherte sonst mit neuer ID — derselbe Druck zählte doppelt
 * gegen die Papierrolle, falls der erste Versuch doch angekommen ist. Die
 * Absicht lebt so lange wie der Tab (nur Arbeitsspeicher).
 *
 * Regeln (wie plotSendeabsichten.ts, schlanker):
 *  - Eine Absicht ist unveränderlich (ID und Angaben). „Erneut speichern“
 *    schickt genau sie; der Server antwortet idempotent mit der Zeile.
 *  - Erfolg löscht sie. Ein Fehler lässt sie stehen.
 *  - Steht ihre ID in einer geladenen Nachtragsliste des Projekts, ist sie
 *    angekommen (auch wenn die Antwort verloren ging) und wird gelöscht —
 *    außer ein Versuch läuft gerade, dann entscheidet dessen Antwort.
 *  - Verwerfen erst, wenn eine Liste erfolgreich und vollständig (nicht am
 *    Limit gekappt) kam, die ID nicht enthält und deren Laden NACH dem
 *    letzten Fehlschlag UND mindestens NACHTRAG_VERWERFEN_FRIST_MS danach
 *    begann. Eine früher begonnene Liste beweist nichts — der Server kann
 *    den Nachtrag erst danach festgeschrieben haben.
 */

/**
 * Frist zwischen Fehlschlag und beweiskräftiger Liste. Bricht die Verbindung
 * im Client ab, kann die Transaktion auf dem Server noch laufen (etwa beim
 * Warten auf die Advisory-Sperre) und erst nach einem Listen-Abgleich
 * festschreiben; die Ereignisnummer sichert nur die Reihenfolge im Client.
 * Supabase bricht Anfragen der Rolle `authenticated` nach rund 8 s ab
 * (`statement_timeout`) — 30 s schließen jede noch laufende Transaktion aus.
 */
export const NACHTRAG_VERWERFEN_FRIST_MS = 30_000;

/**
 * Uhr für die Frist: monoton (`performance.now()`), wie die Timer des
 * Browsers. Eine vorgestellte Geräteuhr verkürzte mit `Date.now()` die Frist,
 * eine zurückgestellte ließe das geplante Nachladen ins Leere laufen
 * (Codex-Review). Gilt nur für Dauern innerhalb dieser Seite — die Absichten
 * leben ohnehin nur im Arbeitsspeicher.
 */
const MONOTON = (): number => performance.now();
let uhr: () => number = MONOTON;

export const nachtragUhrMs = (): number => uhr();

/** Nur für Tests: eigene Uhr, `null` = wieder `performance.now()`. */
export function nachtragUhrFuerTests(f: (() => number) | null): void {
    uhr = f ?? MONOTON;
}

export interface NachtragAngaben {
    /** SiGe-Plan (Version), aus dem nachgetragen wurde. */
    readonly sigeProjektId: string | null;
    readonly titel: string;
    /** Wie eingetippt (Anzeige), z. B. „1,20 m“. */
    readonly laengeText: string;
    readonly laengeMm: number;
    readonly kopien: number;
    /** 'YYYY-MM-DD' */
    readonly datum: string;
    readonly bemerkung: string;
}

export interface NachtragAbsicht {
    readonly id: string;
    readonly projectId: string;
    readonly angaben: NachtragAngaben;
    /** Ein Aufruf mit dieser Absicht läuft gerade (auch aus einem geschlossenen Dialog). */
    readonly laeuft: boolean;
    /** Meldung des letzten Fehlschlags. */
    readonly fehler: string | null;
    /** Ereignisnummer des letzten Fehlschlags (siehe `nachtragLadenBeginnt`). */
    readonly fehlerNr: number | null;
    /** `nachtragUhrMs()` beim letzten Fehlschlag — nur für die lokale Dauer bis zur Frist. */
    readonly fehlerMs: number | null;
    /** Eine beweiskräftige Liste nach dem Fehlschlag kam ohne diese ID. */
    readonly verwerfbar: boolean;
}

/** Beginn eines Ladevorgangs: Reihenfolge (Nummer) und `nachtragUhrMs()`. */
export interface NachtragLadeMarke {
    readonly nr: number;
    readonly ms: number;
}

let stand: ReadonlyMap<string, NachtragAbsicht> = new Map();
let ereignisNr = 0;
const zuhoerer = new Set<() => void>();

const setzen = (neu: Map<string, NachtragAbsicht>) => {
    stand = neu;
    for (const f of zuhoerer) f();
};

const gleicheId = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const loeschen = (projectId: string) => {
    const neu = new Map(stand);
    neu.delete(projectId);
    setzen(neu);
};

/** Für `useSyncExternalStore`: dieselbe Map, bis sich etwas ändert. Schlüssel ist die Projekt-ID. */
export function nachtragAbsichtenStand(): ReadonlyMap<string, NachtragAbsicht> {
    return stand;
}

export function nachtragAbsichtenAbonnieren(f: () => void): () => void {
    zuhoerer.add(f);
    return () => { zuhoerer.delete(f); };
}

/**
 * Ab wann (`nachtragUhrMs()`) eine Liste die Absicht verwerfbar machen kann; `null`,
 * wenn das nicht ansteht (läuft, schon verwerfbar, kein Fehlschlag).
 */
export function nachtragVerwerfenAbMs(a: NachtragAbsicht | null): number | null {
    if (!a || a.laeuft || a.verwerfbar || a.fehlerMs === null) return null;
    return a.fehlerMs + NACHTRAG_VERWERFEN_FRIST_MS;
}

/**
 * Beginnt einen Speicherversuch: die vorhandene Absicht unverändert (ihre
 * Angaben gelten, nicht `angaben`), sonst eine neue. `null`, wenn mit dieser
 * Absicht schon ein Aufruf läuft (Doppelklick, Versuch aus einem
 * geschlossenen Dialog).
 */
export function nachtragAbsichtBeginnen(projectId: string, angaben: NachtragAngaben, neueId: () => string): NachtragAbsicht | null {
    const alt = stand.get(projectId);
    if (alt?.laeuft) return null;
    const absicht: NachtragAbsicht = alt
        ? { ...alt, laeuft: true, fehler: null, verwerfbar: false }
        : { id: neueId(), projectId, angaben, laeuft: true, fehler: null, fehlerNr: null, fehlerMs: null, verwerfbar: false };
    setzen(new Map(stand).set(projectId, absicht));
    return absicht;
}

/** Angekommen: Absicht löschen — nur, wenn noch dieselbe dasteht. */
export function nachtragAbsichtErfolg(projectId: string, id: string): void {
    const alt = stand.get(projectId);
    if (alt && gleicheId(alt.id, id)) loeschen(projectId);
}

/** Fehlgeschlagen oder ungewiss: Absicht bleibt, Verwerfen erst nach Frist und Liste ohne sie. */
export function nachtragAbsichtFehler(projectId: string, id: string, meldung: string, jetztMs: number = nachtragUhrMs()): void {
    const alt = stand.get(projectId);
    if (!alt || !gleicheId(alt.id, id)) return;
    ereignisNr += 1;
    setzen(new Map(stand).set(projectId, {
        ...alt, laeuft: false, fehler: meldung, fehlerNr: ereignisNr, fehlerMs: jetztMs, verwerfbar: false,
    }));
}

/** VOR jedem Laden der Nachtragsliste aufrufen; die Marke geht an `nachtragAbsichtAbgleichen`. */
export function nachtragLadenBeginnt(jetztMs: number = nachtragUhrMs()): NachtragLadeMarke {
    ereignisNr += 1;
    return { nr: ereignisNr, ms: jetztMs };
}

/**
 * Abgleich mit einer ERFOLGREICH geladenen Nachtragsliste des Projekts.
 * Läuft gerade ein Versuch, entscheidet dessen Antwort — nichts ändert sich.
 *  - ID darin → angekommen, gelöscht: `'angekommen'` (gleich, wann das Laden begann).
 *  - ID fehlt, Liste vollständig, Laden begann nach dem letzten Fehlschlag
 *    und mindestens NACHTRAG_VERWERFEN_FRIST_MS danach → verwerfbar.
 * Sonst bleibt alles, wie es ist (`null`).
 */
export function nachtragAbsichtAbgleichen(
    projectId: string, ids: readonly string[], marke: NachtragLadeMarke, vollstaendig: boolean,
): 'angekommen' | null {
    const alt = stand.get(projectId);
    if (!alt || alt.laeuft) return null;
    if (ids.some(id => gleicheId(id, alt.id))) {
        loeschen(projectId);
        return 'angekommen';
    }
    const ab = nachtragVerwerfenAbMs(alt);
    if (vollstaendig && ab !== null && alt.fehlerNr !== null && marke.nr > alt.fehlerNr && marke.ms >= ab) {
        setzen(new Map(stand).set(projectId, { ...alt, verwerfbar: true }));
    }
    return null;
}

/** Verwerfen — nur eine ruhende, nachweislich nicht angekommene Absicht. */
export function nachtragAbsichtVerwerfen(projectId: string, id: string): boolean {
    const alt = stand.get(projectId);
    if (!alt || !gleicheId(alt.id, id) || alt.laeuft || !alt.verwerfbar) return false;
    loeschen(projectId);
    return true;
}

/** Nur für Tests. */
export function nachtragAbsichtenLeeren(): void {
    ereignisNr = 0;
    setzen(new Map());
}
